import {getAccessToken} from "./tokenManager";
import {getBoundFetch} from "./getBoundFetch";

const BASE_URL = "https://api.neuronsearchlab.com/v1";

export const NON_COMMENT_SCOPE = Object.freeze({
  filters: [
    {column: "type", operator: "!=", value: "comment"},
  ],
});

const getFetchImpl = () => getBoundFetch() || fetch;

const appendIfPresent = (params, key, value) => {
  if (value === undefined || value === null || value === "") return;
  params.set(key, String(value));
};

const SHORTHAND_OPERATOR_MAP = Object.freeze({
  "=": ":",
  "!=": "!=",
  "<>": "<>",
  ">": ">",
  "<": "<",
  ">=": ">=",
  "<=": "<=",
});

const isSimpleAndFilter = (filter) => {
  if (!filter || typeof filter !== "object") return false;
  if (typeof filter.column !== "string" || filter.column.trim() === "") return false;
  if (!Object.prototype.hasOwnProperty.call(filter, "value")) return false;
  if (!(filter.operator in SHORTHAND_OPERATOR_MAP)) return false;
  return !filter.logic || String(filter.logic).toUpperCase() === "AND";
};

const stringifyFilterValue = (value) => {
  if (typeof value === "boolean" || typeof value === "number") {
    return String(value);
  }
  return String(value ?? "").trim();
};

const buildShorthandFilter = (filter) => {
  if (!isSimpleAndFilter(filter)) return null;
  const value = stringifyFilterValue(filter.value);
  if (!value) return null;
  return `${filter.column.trim()}${SHORTHAND_OPERATOR_MAP[filter.operator]}${value}`;
};

// Simple AND filters travel as `filter=column:value` shorthand; anything
// richer goes as the full scope object.
const scopeToRequest = (scope) => {
  const scopeFilters = Array.isArray(scope?.filters) ? scope.filters : [];
  if (scopeFilters.length === 0) return {};

  const shorthandFilters = scopeFilters.map(buildShorthandFilter);
  if (shorthandFilters.every(Boolean)) return {filters: shorthandFilters};
  return {scope};
};

const appendScopeOrFilters = (params, scope) => {
  const {filters, scope: fullScope} = scopeToRequest(scope);
  filters?.forEach((filter) => params.append("filter", filter));
  if (fullScope) appendIfPresent(params, "scope", JSON.stringify(fullScope));
};

const getRecommendationEntries = (apiResponse) => {
  if (Array.isArray(apiResponse?.recommendations)) {
    return apiResponse.recommendations;
  }
  if (Array.isArray(apiResponse?.data)) {
    return apiResponse.data;
  }
  return [];
};

const getRecommendationItem = (recommendation) => {
  if (recommendation?.item && typeof recommendation.item === "object") {
    return recommendation.item;
  }
  return recommendation;
};

export const flattenNeuronRecommendationItems = (apiResponse) => {
  return getRecommendationEntries(apiResponse).flatMap((recommendation) => {
    if (Array.isArray(recommendation?.items)) {
      const label = (recommendation.group || "").trim() || "Uncategorised";
      return recommendation.items.map((item) => ({
        id: item.id || item.item_id || item.entity_id || recommendation.item_id,
        score: item.score ?? recommendation.score,
        group: label,
        name: item.name,
        description: item.description,
        metadata: item.metadata,
        recommendationSource: item.source || recommendation.source || "",
        recommendationExplanation:
          item.explanation || recommendation.explanation || item._debug || null,
        raw: item,
      }));
    }

    const item = getRecommendationItem(recommendation);
    const debug = item._debug || recommendation._debug || null;
    return [
      {
        id: item.id || item.item_id || recommendation.item_id || item.entity_id || recommendation.entity_id,
        score: item.score ?? recommendation.score,
        rank: item.rank ?? recommendation.rank ?? null,
        /**
         * The final `score` is a normalised probability across the whole
         * catalogue *after* damping (impression fatigue can multiply it by
         * ~0.07), so it lands around 0.02 and reads as "2% match" even for a
         * strong item. `_debug.base_score` is the item's actual similarity to
         * the reader's profile - that is the number a person means by "match".
         */
        baseScore: debug?.base_score ?? null,
        group: (recommendation.group || item.group || "").trim() || "Uncategorised",
        name: item.name || recommendation.name,
        description: item.description || recommendation.description,
        metadata: item.metadata || recommendation.metadata,
        recommendationSource: item.source || recommendation.source || "",
        recommendationExplanation:
          item.explanation || recommendation.explanation || item._debug || recommendation._debug || null,
        raw: item,
      },
    ];
  });
};

const requestRecommendations = async (params) => {
  const token = await getAccessToken();
  const fetchImpl = getFetchImpl();
  const response = await fetchImpl(`${BASE_URL}/recommendations?${params.toString()}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Failed to fetch recommendations: ${text}`);
  }

  return response.json();
};

const requestOneSet = ({userId, contextId, limit, scope}) => {
  const params = new URLSearchParams();
  appendIfPresent(params, "user_id", userId);
  appendIfPresent(params, "context_id", contextId);
  appendIfPresent(params, "limit", typeof limit === "number" ? limit : undefined);
  appendScopeOrFilters(params, scope);

  return requestRecommendations(params);
};

/**
 * Every rail on the home page asks for its own context when it mounts. Asks
 * made in the same tick go out together as one POST /v1/recommendations/batch
 * (at most MAX_SETS_PER_BATCH per call), so a page of seven rails is one
 * request rather than seven. Each rail is still billed as its own
 * recommendation set. A lone ask keeps the single-set endpoint.
 */
export const MAX_SETS_PER_BATCH = 8;

let pendingSets = [];
let flushScheduled = false;
// Set once the API answers that it has no batch route, so later pages go
// straight to one request per rail.
let batchUnavailable = false;

export const resetRecommendationBatching = () => {
  pendingSets = [];
  flushScheduled = false;
  batchUnavailable = false;
};

const setSignature = ({userId, contextId, limit, scope}) =>
  JSON.stringify([userId ?? "", String(contextId ?? ""), limit ?? null, scope ?? null]);

const settleSingly = (sets) => {
  sets.forEach((set) => {
    requestOneSet(set.input).then(set.resolve, set.reject);
  });
};

const requestBatch = async (sets) => {
  const token = await getAccessToken();
  const fetchImpl = getFetchImpl();
  const body = {
    user_id: sets[0].input.userId,
    requests: sets.map(({input}, index) => ({
      key: String(index),
      context_id: Number(input.contextId),
      ...(typeof input.limit === "number" ? {limit: input.limit} : {}),
      ...scopeToRequest(input.scope),
    })),
  };

  let response;
  try {
    response = await fetchImpl(`${BASE_URL}/recommendations/batch`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    settleSingly(sets);
    return;
  }

  if (!response.ok) {
    if ([403, 404, 405].includes(response.status)) batchUnavailable = true;
    settleSingly(sets);
    return;
  }

  const {results = []} = await response.json();
  const byKey = new Map(results.map((result) => [result.key, result]));
  sets.forEach((set, index) => {
    const result = byKey.get(String(index));
    if (!result || result.status >= 400) {
      const message = result?.error?.message || "Recommendation set failed";
      set.reject(new Error(`Failed to fetch recommendations: ${message}`));
      return;
    }
    set.resolve(result);
  });
};

const flushPendingSets = () => {
  const queued = pendingSets;
  pendingSets = [];
  flushScheduled = false;

  const byUser = new Map();
  queued.forEach((set) => {
    const user = set.input.userId ?? "";
    byUser.set(user, [...(byUser.get(user) || []), set]);
  });

  byUser.forEach((sets) => {
    for (let start = 0; start < sets.length; start += MAX_SETS_PER_BATCH) {
      const chunk = sets.slice(start, start + MAX_SETS_PER_BATCH);
      if (chunk.length === 1 || batchUnavailable) {
        settleSingly(chunk);
      } else {
        requestBatch(chunk).catch((err) => chunk.forEach((set) => set.reject(err)));
      }
    }
  });
};

export const getNeuronRecommendations = (input) => {
  const signature = setSignature(input);
  // Two rails asking for the same thing in the same tick share one answer.
  const same = pendingSets.find((set) => set.signature === signature);
  if (same) return same.promise;

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  pendingSets.push({input, signature, promise, resolve, reject});

  if (!flushScheduled) {
    flushScheduled = true;
    setTimeout(flushPendingSets, 0);
  }
  return promise;
};

export const getAutoNeuronRecommendations = async ({
  userId,
  contextId,
  limit,
  cursor,
  windowDays,
  candidateLimit,
  servedCap,
  scope,
}) => {
  const params = new URLSearchParams();
  params.set("mode", "auto");
  appendIfPresent(params, "user_id", userId);
  appendIfPresent(params, "context_id", contextId);
  appendIfPresent(params, "limit", typeof limit === "number" ? limit : undefined);
  appendIfPresent(params, "cursor", cursor);
  appendIfPresent(params, "window_days", typeof windowDays === "number" ? windowDays : undefined);
  appendIfPresent(
    params,
    "candidate_limit",
    typeof candidateLimit === "number" ? candidateLimit : undefined
  );
  appendIfPresent(params, "served_cap", typeof servedCap === "number" ? servedCap : undefined);
  appendScopeOrFilters(params, scope);

  return requestRecommendations(params);
};
